import { mostReadable, TinyColor } from '@ctrl/tinycolor'
import * as RadixAvatar from '@radix-ui/react-avatar'
import { cn } from '@sparkai/utils'
import cx from 'classnames'
import { ComponentPropsWithoutRef } from 'react'

export interface AvatarProps extends ComponentPropsWithoutRef<'div'> {
  imageProps?: ComponentPropsWithoutRef<'img'>
  fallbackProps?: ComponentPropsWithoutRef<'span'>
  color?: string
  fallback?: {
    delayMs?: number
    value: string
  }
  height?: number | string
  width?: number | string
  textProps?: ComponentPropsWithoutRef<'span'>
  badgeStatus?: {
    status: 'online' | 'idle' | 'offline'
  }
  divClassName?: string
}

export function Avatar({
  color,
  fallback,
  imageProps,
  fallbackProps,
  height = 40,
  width = 40,
  badgeStatus,
  textProps,
  divClassName,
  ...props
}: AvatarProps) {
  const bgColor = color ?? '#6B7280'
  const textColor = mostReadable(new TinyColor(bgColor), ['#FFFFFF', '#000000'])?.toRgbString() ?? '#000000'
  const isSVG = imageProps?.src?.includes('.svg')

  const initials = fallback?.value
    ?.split(' ')
    .map((name) => name.slice(0, 1).toUpperCase())
    .slice(0, 2)
    .join('')

  return (
    <RadixAvatar.Root {...props}>
      <div
        className={cn('chromatic-ignore relative h-auto w-auto', divClassName)}
        style={{
          minHeight: height,
          minWidth: width,
        }}
      >
        <RadixAvatar.Image
          {...imageProps}
          className={cx(imageProps?.className, {
            'inline-block rounded-full': !isSVG,
            'flex items-center justify-center rounded-full p-2': isSVG,
          })}
          src={imageProps?.src}
          style={{
            backgroundColor: isSVG ? bgColor : '',
            width: imageProps?.src ? width : 0,
            height: imageProps?.src ? height : 0,
          }}
          crossOrigin='anonymous'
        />

        <RadixAvatar.Fallback
          {...fallbackProps}
          className={cn('flex items-center justify-center rounded-full p-2', fallbackProps?.className)}
          delayMs={fallback?.delayMs}
          style={{ backgroundColor: bgColor, color: textColor, width, height }}
        >
          <span {...textProps}>{initials}</span>
        </RadixAvatar.Fallback>
        {badgeStatus && (
          <div
            className={cx(
              'absolute bottom-[-2px] right-[-2px] h-[12px] w-[12px] rounded-md border-2 border-white',
              {
                'bg-[#34D399]': badgeStatus.status === 'online',
                'bg-[#D1D5DB]': badgeStatus.status === 'offline',
              }
            )}
          ></div>
        )}
      </div>
    </RadixAvatar.Root>
  )
}
